import React from 'react';

/**
 * DatabaseMetadataBanner — Displays database connection info or hardcoded data warning
 * 
 * Props:
 *   - dbMetadata: { database, username, query_time_ms, host, engine, source } (from backend)
 *   - rowCount: number of rows returned
 *   - isHardcoded: boolean - true if using mock data
 *   - position: 'top' | 'bottom' (default: 'top')
 * 
 * Business Rules:
 *   - Client's own DB (client_config): NEVER show hardcoded, always 100% client DB
 *   - Production DB (secrets_manager): NEVER show hardcoded, always 100% production
 *   - Localhost demo DB (product_type_default): 75% DB / 25% hardcoded
 *   - No DB: 100% hardcoded with warning
 */
const DatabaseMetadataBanner = ({ 
  dbMetadata, 
  rowCount = 0, 
  isHardcoded = false,
  position = 'top' 
}) => {
  // CRITICAL: Never show hardcoded if client has own database
  // This enforces the business rule: client DB = 100% client data
  if (isHardcoded && dbMetadata) {
    const source = dbMetadata.source || 'unknown';
    if (source === 'client_config' || source === 'secrets_manager') {
      // Override: Client has own DB, should NEVER see hardcoded
      console.warn('[DatabaseMetadataBanner] Hardcoded flag set but client has own DB - showing DB banner instead');
      isHardcoded = false;
    }
  }

  // Hardcoded data warning (ONLY for localhost demo DB - 25% case) - RED ALERT
  if (isHardcoded) {
    return (
      <div style={{
        padding: '10px 16px',
        background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
        border: '2px solid #d32f2f',
        borderRadius: '8px',
        marginBottom: position === 'top' ? '16px' : '0',
        marginTop: position === 'bottom' ? '16px' : '0',
        boxShadow: '0 2px 8px rgba(211, 47, 47, 0.15)',
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
        }}>
          <span style={{ fontSize: '24px' }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: 700, 
              color: '#c62828',
              marginBottom: '4px',
            }}>
              Demo Mode: Hardcoded Sample Data
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#d32f2f',
              lineHeight: 1.5,
            }}>
              📋 Demo data: hardcoded • {rowCount.toLocaleString()} rows • No database connected for this client
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: '#e53935',
              marginTop: '6px',
              fontStyle: 'italic',
            }}>
              This is PNP demo data (25% hardcoded for demonstration). Connect your own database via <strong>Update Client</strong> to see 100% live data.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Database connected (75% case) - GREEN SUCCESS with prominent metadata
  if (!dbMetadata) {
    return null;
  }

  const {
    database = 'unknown',
    username = 'unknown',
    query_time_ms = 0,
    host = 'localhost',
    engine = 'postgresql',
    source = 'unknown',
    total_time_ms,
  } = dbMetadata;

  // Determine source badge with business context
  const getSourceBadge = () => {
    if (source === 'client_config') {
      return { 
        label: 'Client DB', 
        color: '#1976d2', 
        icon: '🔵',
        tooltip: '100% your data - no hardcoded responses'
      };
    } else if (source === 'product_type_default') {
      return { 
        label: 'Demo DB', 
        color: '#f57c00', 
        icon: '🟠',
        tooltip: '75% database queries / 25% hardcoded samples'
      };
    } else if (source === 'secrets_manager') {
      return { 
        label: 'Production', 
        color: '#388e3c', 
        icon: '🟢',
        tooltip: '100% production data - no hardcoded responses'
      };
    }
    return { 
      label: 'Connected', 
      color: '#616161', 
      icon: '⚪',
      tooltip: 'Database connected'
    };
  };

  const sourceBadge = getSourceBadge();

  return (
    <div style={{
      padding: '12px 16px',
      background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
      border: '2px solid #4caf50',
      borderRadius: '8px',
      marginBottom: position === 'top' ? '16px' : '0',
      marginTop: position === 'bottom' ? '16px' : '0',
      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.15)',
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: '12px',
      }}>
        <span style={{ fontSize: '24px', marginTop: '2px' }}>✅</span>
        <div style={{ flex: 1 }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            marginBottom: '8px',
          }}>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: 700, 
              color: '#2e7d32',
            }}>
              Database Connected
            </div>
            <span 
              style={{
                fontSize: '10px',
                fontWeight: 600,
                padding: '3px 8px',
                borderRadius: '12px',
                background: sourceBadge.color,
                color: '#fff',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                cursor: 'help',
              }}
              title={sourceBadge.tooltip}
            >
              {sourceBadge.icon} {sourceBadge.label}
            </span>
          </div>

          {/* Database Details Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '8px 16px',
            fontSize: '12px',
            color: '#1b5e20',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontWeight: 600 }}>📊 Database:</span>
              <code style={{ 
                background: 'rgba(46, 125, 50, 0.1)', 
                padding: '2px 6px', 
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '11px',
              }}>
                {database}
              </code>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontWeight: 600 }}>👤 User:</span>
              <code style={{ 
                background: 'rgba(46, 125, 50, 0.1)', 
                padding: '2px 6px', 
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '11px',
              }}>
                {username}
              </code>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontWeight: 600 }}>⚡ Query Time:</span>
              <span style={{ 
                fontWeight: 700, 
                color: query_time_ms < 100 ? '#2e7d32' : query_time_ms < 500 ? '#f57c00' : '#d32f2f',
              }}>
                {query_time_ms}ms
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontWeight: 600 }}>📦 Rows:</span>
              <span style={{ fontWeight: 700 }}>
                {rowCount.toLocaleString()}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontWeight: 600 }}>🔧 Engine:</span>
              <span style={{ textTransform: 'uppercase', fontWeight: 600 }}>
                {engine}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontWeight: 600 }}>🌐 Host:</span>
              <code style={{ 
                background: 'rgba(46, 125, 50, 0.1)', 
                padding: '2px 6px', 
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '11px',
              }}>
                {host}
              </code>
            </div>
          </div>

          {/* Total time if available */}
          {total_time_ms && total_time_ms !== query_time_ms && (
            <div style={{
              marginTop: '8px',
              fontSize: '11px',
              color: '#558b2f',
              fontStyle: 'italic',
            }}>
              Total processing time: {total_time_ms}ms (includes connection + query + processing)
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DatabaseMetadataBanner;
